import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import { useToast } from '../Toast';
import { useAuth } from '../AuthContext';

export default function ProcedureForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const [form, setForm] = useState({
    title: '', content: '', owner_id: '', updater_id: '',
    executor_ids: [], executor_group_ids: [],
    department_ids: [], miro_url: '',
    review_reminder_date: '', review_reminder_time: '09:00',
    create_version: false
  });
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/departments/flat').then(r => setDepartments(r.data)).catch(() => {});
    api.get('/auth/users').then(r => setUsers(r.data)).catch(() => {});
    api.get('/procedures/groups').then(r => setGroups(r.data)).catch(() => {});
    if (isEdit) {
      api.get(`/procedures/${id}`).then(r => {
        const p = r.data;
        const executorUserIds = (p.executors || []).filter(e => e.user_id).map(e => e.user_id);
        const executorGroupIds = (p.executors || []).filter(e => e.group_id).map(e => e.group_id);
        setForm({
          title: p.title || '',
          content: p.content || '',
          owner_id: p.owner_id || '',
          updater_id: p.updater_id || '',
          executor_ids: executorUserIds,
          executor_group_ids: executorGroupIds,
          department_ids: (p.departments || []).map(d => d.id),
          miro_url: p.miro_url || '',
          review_reminder_date: p.review_reminder_date ? p.review_reminder_date.split('T')[0] : '',
          review_reminder_time: p.review_reminder_time || '09:00',
          create_version: false
        });
      });
    } else {
      setForm(f => ({ ...f, owner_id: user?.id || '' }));
    }
  }, [id, isEdit, user]);

  const toggleDept = (deptId) =>
    setForm(f => ({
      ...f,
      department_ids: f.department_ids.includes(deptId)
        ? f.department_ids.filter(d => d !== deptId)
        : [...f.department_ids, deptId]
    }));

  const toggleExecutorUser = (uid) =>
    setForm(f => ({
      ...f,
      executor_ids: f.executor_ids.includes(uid)
        ? f.executor_ids.filter(x => x !== uid)
        : [...f.executor_ids, uid]
    }));

  const toggleExecutorGroup = (gid) =>
    setForm(f => ({
      ...f,
      executor_group_ids: f.executor_group_ids.includes(gid)
        ? f.executor_group_ids.filter(x => x !== gid)
        : [...f.executor_group_ids, gid]
    }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.title.trim()) { toast('Tytuł jest wymagany', 'error'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/procedures/${id}`, form);
        toast('Procedura zaktualizowana');
        nav(`/procedures/${id}`);
      } else {
        const r = await api.post('/procedures', form);
        toast('Procedura utworzona');
        nav(`/procedures/${r.data.id}`);
      }
    } catch (err) {
      toast(err.response?.data?.error || 'Błąd zapisu', 'error');
    } finally {
      setSaving(false);
    }
  };

  const chipStyle = (selected) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer',
    padding: '5px 10px', borderRadius: 6,
    border: `1px solid ${selected ? 'var(--primary)' : 'var(--gray-200)'}`,
    background: selected ? 'var(--primary-light)' : 'white',
    fontSize: 13, userSelect: 'none', marginBottom: 4
  });

  return (
    <div style={{ maxWidth: 840 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => nav(isEdit ? `/procedures/${id}` : '/procedures')}>← Wróć</button>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--gray-900)' }}>
          {isEdit ? 'Edytuj procedurę' : 'Nowa procedura'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic info */}
        <div className="card" style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Podstawowe informacje</h2>
          <div className="form-group">
            <label className="form-label">Tytuł procedury *</label>
            <input
              className="form-input"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Treść (opcjonalnie — pełny dokument wgrywasz jako załącznik)</label>
            <textarea
              className="form-textarea"
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder="Możesz wkleić krótki opis, albo zostawić puste i polegać na załączniku Word..."
              style={{ minHeight: 140 }}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Link Miro (SIPOC)</label>
            <input
              className="form-input"
              value={form.miro_url}
              onChange={e => setForm(f => ({ ...f, miro_url: e.target.value }))}
              placeholder="https://miro.com/app/live-embed/... lub cały iframe"
            />
          </div>
        </div>

        {/* People */}
        <div className="card" style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Odpowiedzialność</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">
                Właściciel
                <span style={{ fontWeight: 400, color: 'var(--gray-400)', marginLeft: 6, fontSize: 12 }}>jedna osoba odpowiedzialna za proces</span>
              </label>
              <select
                className="form-select"
                value={form.owner_id}
                onChange={e => setForm(f => ({ ...f, owner_id: e.target.value }))}
              >
                <option value="">— brak —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">
                Aktualizujący
                <span style={{ fontWeight: 400, color: 'var(--gray-400)', marginLeft: 6, fontSize: 12 }}>odpowiedzialny za przegląd procedury</span>
              </label>
              <select
                className="form-select"
                value={form.updater_id}
                onChange={e => setForm(f => ({ ...f, updater_id: e.target.value }))}
              >
                <option value="">— brak —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>

          {/* Executors — people */}
          {users.length > 0 && (
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label className="form-label">
                Wykonujący — osoby
                <span style={{ fontWeight: 400, color: 'var(--gray-400)', marginLeft: 6, fontSize: 12 }}>wykonują dany proces (można wybrać kilka)</span>
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {users.map(u => (
                  <span key={u.id} style={chipStyle(form.executor_ids.includes(u.id))} onClick={() => toggleExecutorUser(u.id)}>
                    {form.executor_ids.includes(u.id) ? '✓ ' : ''}{u.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Executors — groups */}
          {groups.length > 0 && (
            <div className="form-group">
              <label className="form-label">
                Wykonujący — grupy
                <span style={{ fontWeight: 400, color: 'var(--gray-400)', marginLeft: 6, fontSize: 12 }}>grupy użytkowników</span>
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {groups.map(g => (
                  <span key={g.id} style={chipStyle(form.executor_group_ids.includes(g.id))} onClick={() => toggleExecutorGroup(g.id)}>
                    {form.executor_group_ids.includes(g.id) ? '✓ ' : ''}👥 {g.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Reminder */}
        <div className="card" style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Przypomnienie o przeglądzie</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Data przeglądu</label>
              <input
                className="form-input"
                type="date"
                value={form.review_reminder_date}
                onChange={e => setForm(f => ({ ...f, review_reminder_date: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Godzina przypomnienia</label>
              <input
                className="form-input"
                type="time"
                value={form.review_reminder_time}
                onChange={e => setForm(f => ({ ...f, review_reminder_time: e.target.value }))}
                disabled={!form.review_reminder_date}
              />
            </div>
          </div>
        </div>

        {/* Departments */}
        {departments.length > 0 && (
          <div className="card" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Działy</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {departments.map(d => (
                <label key={d.id} style={chipStyle(form.department_ids.includes(d.id))}>
                  <input
                    type="checkbox"
                    checked={form.department_ids.includes(d.id)}
                    onChange={() => toggleDept(d.id)}
                    style={{ display: 'none' }}
                  />
                  {form.department_ids.includes(d.id) ? '✓ ' : ''}{d.name}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Version option when editing */}
        {isEdit && (
          <div className="card" style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.create_version}
                onChange={e => setForm(f => ({ ...f, create_version: e.target.checked }))}
              />
              <div>
                <div style={{ fontWeight: 500 }}>Zapisz jako nową wersję</div>
                <div style={{ fontSize: 12.5, color: 'var(--gray-400)' }}>Poprzednia wersja zostanie zachowana w historii</div>
              </div>
            </label>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Zapisywanie...' : isEdit ? 'Zapisz zmiany' : 'Utwórz procedurę'}
          </button>
          <button className="btn btn-secondary" type="button" onClick={() => nav(isEdit ? `/procedures/${id}` : '/procedures')}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
